import React, { useState, useRef, useEffect } from "react";
import "./Table.css";

function Table({
  data = [],
  columns = [],
  itemsPerPage = 10,
  currentPage = 1,
  isSortable = false,
  sortColumn = null,
  sortOrder = "asc",
  handleSort = () => {},
  onPageChange = () => {},
  loading = false,
  error = null,
  getActionItems = null,
}) {
  // Menu de contexto global para ações (clique direito)
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, item: null });

  useEffect(() => {
    function handleClick() {
      if (contextMenu.visible) setContextMenu({ ...contextMenu, visible: false });
    }
    function handleScroll() {
      if (contextMenu.visible) setContextMenu({ ...contextMenu, visible: false });
    }
    function handleEsc(e) {
      if (e.key === 'Escape' && contextMenu.visible) setContextMenu({ ...contextMenu, visible: false });
    }
    if (contextMenu.visible) {
      document.addEventListener('mousedown', handleClick);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('keydown', handleEsc);
    }
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('keydown', handleEsc);
    };
  }, [contextMenu]);

  // Handler para clique direito na linha
  function handleRowContextMenu(e, item) {
    if (!getActionItems) return;
    e.preventDefault();
    console.log('[Table] Clique direito na linha:', item);
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      item,
    });
  }

  // Renderização do menu de contexto global
  function ContextActionsMenu() {
    if (!contextMenu.visible || !contextMenu.item) return null;
    const actions = getActionItems(contextMenu.item.id, contextMenu.item) || [];
    if (!actions.length) return null;
    // Ajuste para não sair da tela
    const menuWidth = 200;
    const menuHeight = 40 * actions.length;
    let left = contextMenu.x;
    let top = contextMenu.y;
    if (left + menuWidth > window.innerWidth) left = window.innerWidth - menuWidth - 8;
    if (top + menuHeight > window.innerHeight) top = window.innerHeight - menuHeight - 8;
    return (
      <div
        className="actions-dropdown-list context-actions-menu"
        style={{
          position: 'fixed',
          top: `${top}px`,
          left: `${left}px`,
          zIndex: 10000,
          minWidth: `${menuWidth}px`,
        }}
      >
        {actions.map((action, idx) => {
          console.log('[Table] Renderizando action:', action.label, 'para', contextMenu.item);
          return (
            <button
              key={idx}
              className="action-btn dropdown-action-btn"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('[Table] onMouseDown em action:', action.label, 'para', contextMenu.item);
                setContextMenu({ ...contextMenu, visible: false });
                setTimeout(() => {
                  console.log('[Table] Executando action:', action.label, 'para', contextMenu.item);
                  action.action();
                }, 0);
              }}
              type="button"
            >
              {action.label}
            </button>
          );
        })}
      </div>
    );
  }

  // Renderiza o cabeçalho da tabela
  const renderHeader = () => (
    <thead>
      <tr>
        {columns.map((col) => (
          <th
            key={col.key}
            className={col.sortable ? "sortable" : ""}
            onClick={() => col.sortable && handleSort(col.key)}
          >
            {col.label}
            {isSortable && col.sortable && sortColumn === col.key && (
              <span className="sort-indicator">
                {sortOrder === "asc" ? " ▲" : " ▼"}
              </span>
            )}
          </th>
        ))}
        {getActionItems && <th>Ações</th>}
      </tr>
    </thead>
  );

  // Renderiza as linhas da tabela
  const renderRows = () => {
    if (!data.length) {
      return (
        <tr>
          <td colSpan={columns.length + (getActionItems ? 1 : 0)} className="no-data">
            Nenhum dado encontrado.
          </td>
        </tr>
      );
    }
    return data.map((item, idx) => (
      <tr key={item.id || idx} onContextMenu={(e) => handleRowContextMenu(e, item)}>
        {columns.map((col) => (
          <td key={col.key}>
            {col.render ? col.render(item[col.key], item) : item[col.key]}
          </td>
        ))}
        {getActionItems && (
          <td className="actions-cell">
            <ActionsMenu actions={getActionItems(item.id, item)} />
          </td>
        )}
      </tr>
    ));
  };

  // Menu de ações por linha (dropdown)
  function ActionsMenu({ actions = [] }) {
    const [open, setOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState({});
    const menuRef = useRef(null);
    const btnRef = useRef(null);

    useEffect(() => {
      function handleClickOutside(event) {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
          setOpen(false);
        }
      }
      function handleScroll() {
        setOpen(false);
      }
      if (open) {
        document.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("scroll", handleScroll, true);
      } else {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("scroll", handleScroll, true);
      }
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("scroll", handleScroll, true);
      };
    }, [open]);

    useEffect(() => {
      if (open && btnRef.current && menuRef.current) {
        const btnRect = btnRef.current.getBoundingClientRect();
        const menu = menuRef.current.querySelector('.actions-dropdown-list');
        if (!menu) return;
        const menuHeight = menu.offsetHeight;
        const menuWidth = menu.offsetWidth;
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;
        let top = btnRect.bottom;
        let left = btnRect.left;
        // Se não couber para baixo, abre para cima
        if (btnRect.bottom + menuHeight > windowHeight) {
          top = btnRect.top - menuHeight;
          if (top < 0) top = 8; // margem mínima do topo
        }
        // Se não couber para a direita, ajusta para a esquerda
        if (btnRect.left + menuWidth > windowWidth) {
          left = windowWidth - menuWidth - 8; // margem mínima da direita
        }
        setDropdownStyle({
          position: 'fixed',
          top: `${top}px`,
          left: `${left}px`,
          zIndex: 9999,
          minWidth: `${btnRect.width}px`,
        });
      }
    }, [open]);

    if (!actions.length) return null;
    return (
      <div className="actions-menu-dropdown" ref={menuRef} style={{ position: 'relative' }}>
        <button
          className="action-btn main-action-btn"
          onClick={() => setOpen((v) => !v)}
          type="button"
          ref={btnRef}
        >
          Ações ▾
        </button>
        {open && (
          <div className="actions-dropdown-list" style={dropdownStyle}>
            {actions.map((action, idx) => (
              <button
                key={idx}
                className="action-btn dropdown-action-btn"
                onClick={() => {
                  setOpen(false);
                  action.action();
                }}
                type="button"
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Renderização principal
  return (
    <div className="table-container">
      {loading && <div className="table-loading">Carregando...</div>}
      {error && <div className="table-error">{error}</div>}
      <table className="custom-table">
        {renderHeader()}
        <tbody>{renderRows()}</tbody>
      </table>
      <ContextActionsMenu />
    </div>
  );
}

export default Table;
